/**
 * Portals Admin — List page for managing portals
 *
 * Uses ListPageTemplate for consistent layout, sidenav responsiveness,
 * glassmorphism card, gradient header, search, and table.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Chip, IconButton, Tooltip, Switch } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import KDBox from "components/KDBox";
import KDButton from "components/KDButton";
import KDTypography from "components/KDTypography";
import ListPageTemplate from "templates/ListPageTemplate";
import { useMaterialUIController } from "context";
import { useAbility, ACTIONS, SUBJECTS } from "context/AbilityContext";
import { useT } from "context/I18nContext";
import usePortals from "hooks/usePortals";
import logger from "services/logger";

function Portals() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { palette } = useTheme();
  const navigate = useNavigate();
  const ability = useAbility();
  const t = useT();

  // Local snackbar state — rendered by ListPageTemplate via KDSnackbar
  const [snackbar, setSnackbar] = useState({ open: false, severity: "info", title: "", message: "" });
  const showSnackbar = useCallback(
    (severity, title, message) => setSnackbar({ open: true, severity, title, message }),
    []
  );

  const { listPortals, updatePortal, deletePortal, loading } = usePortals();

  const [portals, setPortals] = useState([]);
  const [loadError, setLoadError] = useState("");

  const canCreate = ability.can(ACTIONS.CREATE, SUBJECTS.PORTAL);
  const canUpdate = ability.can(ACTIONS.UPDATE, SUBJECTS.PORTAL);
  const canDelete = ability.can(ACTIONS.DELETE, SUBJECTS.PORTAL);

  const loadPortals = useCallback(async () => {
    try {
      setLoadError("");
      const data = await listPortals();
      setPortals(data);
    } catch (err) {
      logger.error("[Portals]", "Failed to load portals:", err.message);
      setLoadError(err.message || "Failed to load portals");
      showSnackbar("error", "Error", t("portals.loadError") || "Failed to load portals");
    }
  }, [listPortals, showSnackbar, t]);

  useEffect(() => {
    loadPortals();
  }, [loadPortals]);

  const handleToggleActive = useCallback(
    async (e, portal) => {
      e.stopPropagation();
      try {
        await updatePortal(portal.id, { isActive: !portal.isActive });
        setPortals((prev) =>
          prev.map((p) => (p.id === portal.id ? { ...p, isActive: !p.isActive } : p))
        );
        showSnackbar(
          "success",
          "Success",
          portal.isActive
            ? t("portals.deactivated") || "Portal deactivated"
            : t("portals.activated") || "Portal activated"
        );
      } catch (err) {
        showSnackbar("error", "Error", t("portals.toggleError") || "Failed to toggle portal");
      }
    },
    [updatePortal, showSnackbar, t]
  );

  const handleDelete = useCallback(
    async (e, portal) => {
      e.stopPropagation();
      if (!window.confirm(`Delete portal "${portal.name}"? This cannot be undone.`)) return;
      try {
        await deletePortal(portal.id);
        setPortals((prev) => prev.filter((p) => p.id !== portal.id));
        showSnackbar("success", "Success", t("portals.deleted") || "Portal deleted");
      } catch (err) {
        showSnackbar("error", "Error", t("portals.deleteError") || "Failed to delete portal");
      }
    },
    [deletePortal, showSnackbar, t]
  );

  // --- Table columns ---
  const columns = useMemo(
    () => [
      {
        field: "name",
        header: t("portals.colName") || "Name",
        sortable: true,
        render: (val, row) => (
          <KDBox display="flex" flexDirection="column">
            <KDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "dark"}>
              {val}
            </KDTypography>
            {row.description && (
              <KDTypography variant="caption" color="text" sx={{ opacity: 0.7, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {row.description}
              </KDTypography>
            )}
          </KDBox>
        ),
      },
      {
        field: "slug",
        header: t("portals.colSlug") || "Slug",
        sortable: true,
        render: (val) => (
          <KDTypography variant="caption" fontWeight="medium" color={darkMode ? "white" : "dark"} sx={{ fontFamily: "monospace" }}>
            /{val}
          </KDTypography>
        ),
      },
      {
        field: "isActive",
        header: t("portals.colStatus") || "Status",
        sortable: true,
        render: (val) => (
          <Chip
            label={val ? (t("portals.active") || "Active") : (t("portals.inactive") || "Inactive")}
            size="small"
            color={val ? "success" : "default"}
            sx={{ fontWeight: 600, fontSize: "0.7rem" }}
          />
        ),
      },
      {
        field: "pages",
        header: t("portals.colPages") || "Pages",
        sortable: true,
        render: (val) => (
          <KDTypography variant="button" color={darkMode ? "white" : "dark"}>
            {(val || []).length}
          </KDTypography>
        ),
      },
      {
        field: "roles",
        header: t("portals.colRoles") || "Roles",
        render: (val) => (
          <KDBox display="flex" gap={0.5} flexWrap="wrap">
            {(val || []).length > 0 ? (
              val.map((role) => (
                <Chip key={role} label={role} size="small" variant="outlined" sx={{ fontSize: "0.65rem", height: 22 }} />
              ))
            ) : (
              <KDTypography variant="caption" color="text" sx={{ opacity: 0.5 }}>—</KDTypography>
            )}
          </KDBox>
        ),
      },
      {
        field: "actions",
        header: "",
        render: (_val, row) => (
          <KDBox display="flex" alignItems="center" gap={0.5} justifyContent="flex-end">
            {canUpdate && (
              <Tooltip title={t("portals.toggleActive") || "Toggle active"}>
                <Switch
                  size="small"
                  checked={row.isActive}
                  onChange={(e) => handleToggleActive(e, row)}
                  onClick={(e) => e.stopPropagation()}
                  color="success"
                />
              </Tooltip>
            )}
            <Tooltip title={t("portals.view") || "View portal"}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); window.open(`/portal/${row.slug}`, "_blank"); }}
              >
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("common.edit") || "Edit"}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); navigate(`/admin/portals/${row.id}`); }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canDelete && (
              <Tooltip title={t("common.delete") || "Delete"}>
                <IconButton
                  size="small"
                  onClick={(e) => handleDelete(e, row)}
                  sx={{ color: "error.main" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </KDBox>
        ),
      },
    ],
    [darkMode, canUpdate, canDelete, handleToggleActive, handleDelete, navigate, t]
  );

  const rows = useMemo(() => portals.map((p) => ({ ...p })), [portals]);

  return (
    <ListPageTemplate
      title={t("portals.title") || "Portals"}
      columns={columns}
      rows={rows}
      loading={loading}
      error={loadError}
      emptyMessage={t("portals.empty") || "No portals created yet"}
      onRowClick={(row) => navigate(`/admin/portals/${row.id}`)}
      searchable
      searchPlaceholder={t("portals.searchPlaceholder") || "Search portals..."}
      defaultSortField="name"
      headerColor="info"
      defaultVisibleFields={["name", "slug", "isActive", "pages", "roles", "actions"]}
      snackbar={snackbar}
      onSnackbarClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      extraToolbarActions={
        canCreate ? (
          <KDButton
            variant="contained"
            size="small"
            onClick={() => navigate("/admin/portals/new")}
            sx={{
              color: palette.white.main,
              backgroundColor: palette.headerOverlay.button,
              borderRadius: "10px",
              "&:hover": { backgroundColor: palette.headerOverlay.buttonHover },
            }}
          >
            <AddIcon sx={{ mr: 0.5 }} /> {t("portals.create") || "New Portal"}
          </KDButton>
        ) : null
      }
    />
  );
}

export default Portals;
